import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { withStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from "@material-ui/core/Typography";
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FilterListView from '../../components/FilterListView';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';

import DataFrameDownload from '../../components/utils/DataFrameDownload'
import Scenario from '../../api/Scenario';
import Header from '../../components/utils/Header';
import {
  formatVolume,
  formatRatioPercent,
  unitFormatter,
} from '../../utils';
import { GEOGRAPHY_LABELS } from '../../labels';
import ConditionalFormat from '../../components/utils/ConditionalFormat';
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import DemandSwitches from '../../store/DemandSwitches';
import DemandSwitchesPanel from '../../components/DemandSwitchesPanel';


const styles = theme => ({
  chartContainer: {
    overflow: 'hidden',
  },
  tablePaper: {
    overflow: 'auto',
  },
  tableSeparator: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  subtotalColor: {
    backgroundColor: theme.palette.grey[100],
  },
  totalColor: {
    backgroundColor: theme.palette.grey[300],
  },
  autoRowSize: {
    tableLayout: 'auto',
    width: 'auto',
  },
});


@withStyles(styles)
@withRouter
@observer
class DemandDetailView extends BaseScenarioDashboardView {
  static apiSubPath = '/demand_detail/';

  @observable scenario1 = null;
  @observable scenario2 = null;
  @observable geographicalLevel = null;

  constructor(props) {
    super(props);
    this.switches = new DemandSwitches({}, this.updateData);
  }

  _setData(data) {
    this.scenario1 = data.scenario1;
    this.scenario2 = data.scenario2;
    this.geographicalLevel = data.geographical_level;
    this.switches.set('Base demand in scope', data.base_demand_in_scope);
    this.switches.set('Base demand out of scope', data.base_demand_out_scope);
    this.switches.set('Innovation in scope', data.innovation_demand);
    this.switches.set('Risks & opps in scope', data.risks_opps_demand);
  }

  _getQueryParams() {
    return {
      scenario1: this.scenario1,
      scenario2: this.scenario2,
      geographical_level: this.geographicalLevel,
      base_demand_in_scope: this.switches.get('Base demand in scope'),
      base_demand_out_scope: this.switches.get('Base demand out of scope'),
      innovation_demand: this.switches.get('Innovation in scope'),
      risks_opps_demand: this.switches.get('Risks & opps in scope'),
    };
  }

  onScenario1Change = this.genericChangeHandler('scenario1');
  onScenario2Change = this.genericChangeHandler('scenario2');
  onGeographicalLevelChange = this.genericChangeHandler('geographicalLevel');

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          <ScenarioSelect
            label="Scenario 1"
            scenarioId={this.scenario1}
            choices={this.data.scenario1_choices}
            onScenarioChange={this.onScenario1Change}
          />
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 2"
            scenarioId={this.scenario2}
            choices={this.data.scenario2_choices}
            onScenarioChange={this.onScenario2Change}
          />
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            'Geographical level', this.data.geographical_level_choices,
            this.geographicalLevel, this.onGeographicalLevelChange,
          )}
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
        <DataFrameDownload
            href={Scenario.getApiUrl('/demand_detail/?' + this.getQueryString())}
            permission="athena.download_demand_detail" 
          />
        </Grid>
      </Grid>
    );
  }

  get chart() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const formatter = unitFormatter('t', 0);
    return (
      <ResponsiveContainer aspect={21 / 9}>
        <ComposedChart data={[...this.data.graph_data]} margin={{ top: 16, left: 30 }}>
          <XAxis dataKey="month_year" />
          <YAxis tickFormatter={formatter} orientation="left" />
          <RechartsTooltip formatter={formatter} />
          <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
          {this.getLine('Scenario 1', { fill: 'blue', strokeWidth: 2, formatter })}
          {
            this.scenario2 === null
              ? null
              : this.getBar('Scenario 2', { fill: 'rgba(233, 30, 99, 0.8)', formatter })
          }
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  get lvl4Table() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const { classes } = this.props;
    const headers = Object.keys(this.data.lvl4_table[0]).filter(
      column => !column.endsWith(' (%)')
    );
    const isIndex = key => key === 'LVL4';
    const getCellClass = key => (
      isIndex(key) || key.endsWith('Scenario 2 - 1')
        ? classes.tableSeparator
        : null
    );
    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right',
      className: getCellClass(key),
    });
    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };
    const getValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = formatVolume(row[key], 't', 0);
      const percentKey = key + ' (%)';
      if (percentKey in row) {
        return (
          <ConditionalFormat value={row[percentKey]} name="demand-detail-lvl4">
            {formattedValue} ({formatRatioPercent(row[percentKey])})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>LVL4 Table</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          {/* TODO : replace with a DataFrame*/}
          <Table stickyHeader size="small" className={classes.autoRowSize}>
            <TableHead>
              <TableRow>
                {headers.map(key => (
                  <TableCell {...getCellProps(key)}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.data.lvl4_table.map((row, index) => (
                <TableRow key={index} className={paintRow(row['LVL4'])}>
                  {headers.map(key =>
                    <TableCell {...getCellProps(key)}>
                      {getValue(row, key)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    );
  }

  get stageTable() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const { classes } = this.props;
    const headers = Object.keys(this.data.stage_table[0]).filter(
      column => !column.endsWith(' (%)')
    );
    const isIndex = key => key === 'stage';
    const getCellClass = key => (
      isIndex(key) || key.endsWith('Scenario 2 - 1')
        ? classes.tableSeparator
        : null
    );
    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };

    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right',
      className: getCellClass(key),
    });

    const getValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = formatVolume(row[key], 't', 0);
      const percentKey = key + ' (%)';
      if (percentKey in row) {
        return (
          <ConditionalFormat value={row[percentKey]} name="demand-detail-lvl4">
            {formattedValue} ({formatRatioPercent(row[percentKey])})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Stage Table</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          {/* TODO : replace with a DataFrame*/}
          <Table stickyHeader size="small" className={classes.autoRowSize}>
            <TableHead>
              <TableRow>
                {headers.map(key => (
                  <TableCell {...getCellProps(key)}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.data.stage_table.map((row, index) => (
                <TableRow key={index} className={paintRow(row['stage'])}>
                  {headers.map(key =>
                    <TableCell {...getCellProps(key)}>
                      {getValue(row, key)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    );
  }

  get factoryTable() {
    const { classes } = this.props;
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const headers = Object.keys(this.data.factory_table[0]).filter(
      column => !column.endsWith(' (%)')
    );
    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };

    const isIndex = key => (key === 'Factory') || (key === 'Market Name') || (key === 'CBU Name') || (key === 'Macro Zone');
    const getCellClass = key => (
      isIndex(key) || key.endsWith('Scenario 2 - 1')
        ? classes.tableSeparator
        : null
    );

    const getValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = formatVolume(row[key], 't', 0);
      const percentKey = key + ' (%)';
      if (percentKey in row) {
        return (
          <ConditionalFormat value={row[percentKey]} name="demand-detail-factory">
            {formattedValue} ({formatRatioPercent(row[percentKey])})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };
    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right',
      className: getCellClass(key),
    });

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Factory table</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          {/* TODO : replace with a DataFrame*/}
          <Table stickyHeader size="small" className={classes.autoRowSize}>
            <TableHead>
              <TableRow>
                {headers.map(key => (
                  <TableCell {...getCellProps(key)}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.data.factory_table.map((row, index) => (
                <TableRow key={index} className={paintRow(row[GEOGRAPHY_LABELS[this.data.geographical_level]])}>
                  {headers.map(key => (
                    <TableCell {...getCellProps(key)}>{getValue(row, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
        </Table>
        </AccordionDetails>
      </Accordion>
    );
  }

  get technologyTable() {
    const { classes } = this.props;
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const headers = Object.keys(this.data.technology_table[0]).filter(
      column => !column.endsWith(' (%)')
    );
    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };

    const isIndex = key => (['Technology', 'Market Name', 'CBU Name', 'Macro Zone'].includes(key));
    const getCellClass = key => (
      isIndex(key) || key.endsWith('Scenario 2 - 1')
        ? classes.tableSeparator
        : null
    );

    const getValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = formatVolume(row[key], 't', 0);
      const percentKey = key + ' (%)';
      if (percentKey in row) {
        return (
          <ConditionalFormat value={row[percentKey]} name="demand-detail-factory">
            {formattedValue} ({formatRatioPercent(row[percentKey])})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };
    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right',
      className: getCellClass(key),
    });

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Technology table</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          {/* TODO : replace with a DataFrame*/}
          <Table stickyHeader size="small" className={classes.autoRowSize}>
            <TableHead>
              <TableRow>
                {headers.map(key => (
                  <TableCell {...getCellProps(key)}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.data.technology_table.map((row, index) => (
                <TableRow key={index} className={paintRow(row[GEOGRAPHY_LABELS[this.data.geographical_level]])}>
                  {headers.map(key => (
                    <TableCell {...getCellProps(key)}>{getValue(row, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
        </Table>
        </AccordionDetails>
      </Accordion>
    );
  }

  get geoTable() {
    const { classes } = this.props;
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const headers = Object.keys(this.data.geo_table[0]).filter(
      column => !column.endsWith(' (%)')
    );
    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };

    const isIndex = key => (['Market Name', 'CBU Name', 'Macro Zone'].includes(key));
    const getCellClass = key => (
      isIndex(key) || key.endsWith('Scenario 2 - 1')
        ? classes.tableSeparator
        : null
    );

    const getValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = formatVolume(row[key], 't', 0);
      const percentKey = key + ' (%)';
      if (percentKey in row) {
        return (
          <ConditionalFormat value={row[percentKey]} name="demand-detail-factory">
            {formattedValue} ({formatRatioPercent(row[percentKey])})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };
    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right',
      className: getCellClass(key),
    });

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Geography table</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          {/* TODO : replace with a DataFrame*/}
          <Table stickyHeader size="small" className={classes.autoRowSize}>
            <TableHead>
              <TableRow>
                {headers.map(key => (
                  <TableCell {...getCellProps(key)}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.data.geo_table.map((row, index) => (
                <TableRow key={index} className={paintRow(row[GEOGRAPHY_LABELS[this.data.geographical_level]])}>
                  {headers.map(key => (
                    <TableCell {...getCellProps(key)}>{getValue(row, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
        </Table>
        </AccordionDetails>
      </Accordion>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Demand Detail" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        <Grid item>
          <DemandSwitchesPanel switches={this.switches} />
        </Grid>
        <Grid item className={classes.chartContainer}>
          {this.chart}
        </Grid>
        <Grid item>
          {this.lvl4Table}
          {this.stageTable}
          {this.factoryTable}
          {this.technologyTable}
          {this.geoTable}
        </Grid>
      </Grid>
    );
  }
}


export default DemandDetailView;
